import React from "react";
import { useIntl } from "react-intl";
import { useAtomValue } from "jotai";
import {
  useOperatingContext,
  disableNavState,
  segment,
  useInsightsUrl,
  useShowChat,
  useFeatureFlags,
} from "@granular/fabric3-core";
import { GKLayoutShell, FlexibleNavItem, GKIconName } from "@granular/gds";
import {
  FabricApp,
  CORE_NAV_GROUPINGS_AND_ORDER,
} from "@granular/fabric3-definitions";

import { APPS_LOWER_SIMPLE } from "../constants";
import { checkAppPermissions, goToClassicInsights } from "../helpers";
import Dot, { getDotColorAndText, DotColor } from "./Dot";

type FabricLayoutShellProps = {
  children: React.ReactNode;
  appRuntimeInfo: FabricApp[];
  home?: { to: string };
  alertsOverlay?: boolean;
  isLocalhost: boolean;
};

/**
 * The Fabric implementation of the GKLayoutShell. This component exists
 * because we need to use useOperatingContext (for permissions) that uses
 * a useSuspenseQuery, and therefore must be in a separate component so we
 * can properly wrap it in a <Suspense> component (done in <Container />).
 */
export const FabricLayoutShell: React.FunctionComponent<
  FabricLayoutShellProps
> = ({
  children,
  appRuntimeInfo,
  home,
  alertsOverlay,
  isLocalhost,
}: FabricLayoutShellProps) => {
  const intl = useIntl();
  const { userCan } = useOperatingContext();
  const disableNav = useAtomValue(disableNavState);
  const insightsUrl = useInsightsUrl();
  const { "enable-hide-classic-link": hideClassicLink } = useFeatureFlags<{
    "enable-hide-classic-link": boolean;
  }>();

  /**
   * Second and Final transformation (first in <Container />). We use the
   * object above to tell GDS how to draw the sidebar/Core Nav. In order, we
   *
   * 1) Tell sort the apps by their groupings. If a grouping is `null` (i.e. an
   *    app is in the upper third of the Core Nav), we assign a dummy grouping
   *    called `NO_GROUPING`
   *
   * 2) Sort by weight within groupings
   *
   * 3) Prune any empty groupings.
   */
  type WeightedNavItem = Extract<FlexibleNavItem, { hidden?: boolean }> & {
    weight: number;
  };

  /**
   * Helper function to translate grouping keys. We basically want to translate
   * any groupings that are set but don't start with an underscore. Underscore
   * groupings are special.
   */
  const getTranslatedGrouping = (grouping: string | null): string => {
    if (!grouping) {
      return "NO_GROUPING";
    }
    if (grouping.startsWith("_")) {
      // Untranslated groupings
      return grouping;
    }
    return intl.formatMessage({ id: grouping }).toUpperCase();
  };

  const coreNavItems = Object.fromEntries([
    ...CORE_NAV_GROUPINGS_AND_ORDER.map((key) => [
      getTranslatedGrouping(key),
      [],
    ]),
    ["NO_GROUPING", []],
  ]) as Record<string, WeightedNavItem[]>;

  for (const app of appRuntimeInfo) {
    const hasPermission = app.permissions
      ? checkAppPermissions(app.permissionModel, userCan)
      : true;
    const [dotColor, dotText] = getDotColorAndText(
      app.visible,
      app.routable,
      hasPermission,
    );

    const translatedGrouping = getTranslatedGrouping(app.grouping);
    coreNavItems[translatedGrouping]?.push({
      to: app.to,
      label: app.label,
      icon: app.icon as GKIconName,
      weight: app.weight,
      tooltip:
        app.visible && !app.routable
          ? intl.formatMessage({
              id: "insightsView_sidebar_comingsoon",
            })
          : undefined,

      hidden: isLocalhost ? false : !(app.visible && hasPermission),
      disabled: isLocalhost ? false : !(app.routable && hasPermission),
      prepend: isLocalhost ? (
        <Dot
          color={dotColor as DotColor}
          toolTipText={dotText}
          key={app.label}
        />
      ) : undefined,
      badge: app.badge?.label,
      badgeColor: app.badge?.color,
      "data-qa": `nav-item-${app.namespace}`,
    });
  }

  // Sort groupings by weight. Remove any empty groupings.
  for (const group in coreNavItems) {
    const g = group;

    if (coreNavItems[g] !== undefined) {
      coreNavItems[g].sort((a, b) =>
        a.weight > b.weight ? 1 : a.weight < b.weight ? -1 : 0,
      );

      if (coreNavItems[g].filter((i) => !i.hidden).length === 0) {
        delete coreNavItems[g];
      }
    }
  }

  const topNavItems = coreNavItems.NO_GROUPING;
  /**
   * Nav items grouped to show in the main section of the navbar, "top"
   */
  const groupedNavTopItems = Object.fromEntries(
    Object.entries(coreNavItems).filter(
      ([key]) => key !== "NO_GROUPING" && !key.startsWith("_"),
    ),
  );

  /**
   * Nav items to be shown in the lower section of the navbar, "lower". These are
   * items grouped with the "_LOWER" grouping key. They are already sorted at this point
   * so we can just flat map them out and they will be in the right order.
   *
   * NOTE: This is a list instead of an Record like above.
   */
  const groupedLowerNavItems: FlexibleNavItem[] = Object.entries(coreNavItems)
    .filter(([key]) => key === "_LOWER")
    .flatMap(([, items]) => items);

  /**
   * Most of the sidebar is generated from the apps themselves, because a menu
   * item must have an app/component to point to. But in some cases, you may
   * want to add some links that are not part of the apps.
   *
   * Notice that most of these NavItems that are created outside the scope of
   * the actual Container component will have to be moved inside of it or of a
   * hook, and placed within the i18n wrapper in order to implement
   * translations.
   *
   * We can also 'seed' the sidebar and navigation with a link to the default
   * app, whatever Product decides it to be. At the moment, it's empty. The "/"
   * or "Home" link can be accessed by clicking the Granular Logo.
   */
  const APPS_UPPER: Record<number | string, FlexibleNavItem[]> = {
    0: hideClassicLink
      ? []
      : [
          {
            href: "#",
            icon: GKIconName.KeyboardArrowLeft,
            label: intl.formatMessage({ id: "classic_view_label" }),
            tooltip: intl.formatMessage({
              id: "Navigate_to_features_in_the_older_version_of_Granular_Insights",
            }),
            className: "back-to-classic",
            onClick: () => {
              try {
                void segment.track("select Back to Classic", {
                  currentApp: globalThis.location.pathname.split("/")[1],
                });
              } catch (error) {
                console.warn(`Failed to track event ${JSON.stringify(error)}`);
              } finally {
                goToClassicInsights(insightsUrl);
              }
            },
          },
        ],
  };

  /**
   * Insert any apps that specify a `null` grouping. Sorting _was_ done
   * internally within the temporary "NO_GROUPING" group.
   */
  if (topNavItems?.length) {
    APPS_UPPER[1] = topNavItems;
  }

  /**
   * These are towards the bottom of the sidebar. Stuff like settings, help,
   * etc.
   *
   * NOTE: The value of the key does not matter in this case. This is a single
   * sorted grouping instead of multiple. Pick your favorite number 🎲
   */
  const APPS_LOWER: Record<number, FlexibleNavItem[]> = {
    1000: Object.values(groupedLowerNavItems),
  };
  for (const fabricApp of APPS_LOWER_SIMPLE) {
    /**
     * We use a loop and type this explicitly to empower TypeScript. Using
     * `Array.map` to iterate over `APPS_LOWER_SIMPLE` doesn't work for reasons
     * we do not understand yet.
     */
    const lowerApp: FlexibleNavItem = {
      to: fabricApp.href ?? fabricApp.route ?? "#",
      icon: GKIconName[fabricApp.icon as keyof typeof GKIconName],
      label: intl.formatMessage({ id: fabricApp.label }),
    };

    APPS_LOWER[1000]!.push(lowerApp);
  }

  // Handle CARL chatbot permission logic
  const { canViewChat } = useShowChat();

  // Special considerations for CARL link
  if (APPS_LOWER[1000] !== undefined) {
    const carlItemIndex = APPS_LOWER[1000].findIndex(
      (app) => "label" in app && app.label.toUpperCase().includes("CARL"),
    );
    // CARL's visibility gets handled differently than other apps. We check
    // for access then we also check if we're using the
    const carlHidden = !isLocalhost && !canViewChat;
    const carlSidenavItemWithCustomHidden = APPS_LOWER[1000][carlItemIndex]
      ? {
          ...APPS_LOWER[1000][carlItemIndex],
          hidden: carlHidden,
        }
      : undefined;

    if (carlItemIndex !== -1 && carlSidenavItemWithCustomHidden) {
      APPS_LOWER[1000][carlItemIndex] = carlSidenavItemWithCustomHidden;
    }
  }

  return (
    <GKLayoutShell
      alertsOverlay={alertsOverlay}
      home={home}
      primaryNavItems={
        disableNav ? undefined : { ...APPS_UPPER, ...groupedNavTopItems }
      }
      secondaryNavItems={disableNav ? undefined : APPS_LOWER}
    >
      {children}
    </GKLayoutShell>
  );
};
